import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Plus, CheckCircle, AlertTriangle, XCircle, Scan } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { uploadPhotoMulti, listPhotos, deletePhotoSlot, syncFaceToHF, deleteFaceFromHF, syncSupabaseEmbedding } from '../lib/storage';
import { cleanEnv } from '../lib/envClean';
import { useApp } from '../contexts/AppContext';

// VITE_HF_SPACE_URL hanya untuk display — analyze pakai Vercel proxy /api/analyze
// supaya HF_TOKEN tidak expose ke browser (token server-only).
const HF_URL = cleanEnv(import.meta.env.VITE_HF_SPACE_URL) || '';

const SLOT_STATE = {
  uploading: { color: '#00d4ff', label: 'Mengunggah...' },
  scanning:  { color: '#7c4dff', label: 'AI Memindai...' },
  syncing:   { color: '#00d4ff', label: 'Sinkronisasi...' },
  ok:        { color: '#00ff88', label: 'Tersimpan' },
  no_face:   { color: '#ffd60a', label: 'Tidak ada wajah' },
  fail:      { color: '#ff2d55', label: 'Gagal' },
};

// 5 angle pose untuk biometric reference per CSOO directive 2026-05-09.
// Setiap subject di datacenter HARUS punya 5 foto angle berbeda supaya
// face matching Stage 1 OSINT punya recall optimal — kandidat dgn pose
// menghadap atas/bawah/samping tetap match meskipun bukan frontal.
//
// Slot 0..4 → label angle. Storage path tetap penduduk-photos/{nik}/{slot}.{ext}.
export const POSE_LABELS = {
  0: 'DEPAN',   // wajah menghadap kamera
  1: 'KIRI',    // profil kiri 90°
  2: 'KANAN',   // profil kanan 90°
  3: 'ATAS',    // mendongak ke atas (chin up)
  4: 'BAWAH',   // menunduk ke bawah (chin down)
};
export const POSE_HINTS = {
  0: 'Wajah menghadap kamera',
  1: 'Profil kiri 90°',
  2: 'Profil kanan 90°',
  3: 'Mendongak ke atas',
  4: 'Menunduk ke bawah',
};
const MAX_PHOTO_SLOTS = 5;

async function analyzePhoto(file) {
  // Pakai Vercel proxy /api/analyze — HF_TOKEN aman di server, tidak expose ke browser.
  // Fallback ke direct HF call hanya kalau VITE_HF_SPACE_URL diset (dev mode).
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/analyze', {
    method: 'POST', body: fd, signal: AbortSignal.timeout(90000),
  });
  if (!res.ok) throw new Error(`AI ${res.status}`);
  return res.json();
}

function cosineSim(a, b) {
  let dot = 0, na = 0, nb = 0;
  for (let i = 0; i < a.length; i++) { dot += a[i]*b[i]; na += a[i]*a[i]; nb += b[i]*b[i]; }
  return dot / (Math.sqrt(na * nb) + 1e-6);
}

// Validasi: embedding harus array 512 angka finite (bukan NaN/Infinity/null).
// Melindungi dari corrupt vector yang bisa merusak average embedding.
function isValidEmb(e) {
  return Array.isArray(e) && e.length === 512 && e.every(v => typeof v === 'number' && isFinite(v));
}

function averageNormalize(a, b) {
  const avg = a.map((v, i) => (v + b[i]) / 2);
  const norm = Math.sqrt(avg.reduce((s, v) => s + v*v, 0)) || 1;
  return avg.map(v => v / norm);
}

async function storeEmbeddingInSupabase(nik, newEmb) {
  if (!isValidEmb(newEmb)) {
    throw new Error('Embedding dari AI Engine tidak valid. Coba foto yang lebih jelas.');
  }
  const { data: row } = await supabase.from('penduduk').select('face_embedding').eq('nik', nik).single();
  const oldEmb = row?.face_embedding;
  // Hanya average kalau embedding lama juga valid — cegah corrupt vector merusak hasil.
  const merged = (oldEmb && isValidEmb(oldEmb)) ? averageNormalize(oldEmb, newEmb) : newEmb;
  // .select() supaya tahu berapa baris ke-update. Kalau 0 → row penduduk belum ada
  // (mis. foto diupload sebelum data disimpan) → JANGAN klaim sukses. Ini cegah
  // false-positive "Tersinkron" padahal embedding tidak tersimpan.
  const { data: updated, error } = await supabase
    .from('penduduk').update({ face_embedding: merged }).eq('nik', nik).select('nik');
  if (error) throw error;
  if (!updated || updated.length === 0) {
    throw new Error('Data penduduk belum tersimpan. Simpan dulu sebelum mengunggah foto.');
  }
}

// GUARDRAIL: kalau /api/analyze gagal atau tidak return embedding,
// fallback ke Supabase Edge Function `extract-face-embedding` yang akses
// foto langsung dari Storage (server-to-server) → encode via HF → simpan.
// Ini memastikan embedding TIDAK PERNAH miss meski client-path bermasalah
// (HF cold, network blip, browser CORS, dll).
async function triggerEdgeFunctionFallback(nik, storagePath) {
  try {
    const { data, error } = await supabase.functions.invoke('extract-face-embedding', {
      body: { penduduk_id: nik, bucket: 'penduduk-photos', path: storagePath },
    });
    if (error) {
      console.warn('[edge-fn fallback] invoke error:', error.message);
      return { ok: false };
    }
    return data || { ok: false };
  } catch (e) {
    console.warn('[edge-fn fallback] exception:', e?.message);
    return { ok: false };
  }
}

const ScanToast = ({ toast, onDismiss }) => {
  if (!toast) return null;
  const cfg = {
    scanning: { icon: <div className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#7c4dff', borderTopColor: 'transparent' }} />, color: '#7c4dff', bg: 'rgba(124,77,255,0.12)', border: 'rgba(124,77,255,0.35)' },
    ok:       { icon: <CheckCircle size={13} color="#00ff88" />, color: '#00ff88', bg: 'rgba(0,255,136,0.10)', border: 'rgba(0,255,136,0.35)' },
    no_face:  { icon: <AlertTriangle size={13} color="#ffd60a" />, color: '#ffd60a', bg: 'rgba(255,214,10,0.10)', border: 'rgba(255,214,10,0.35)' },
    fail:     { icon: <XCircle size={13} color="#ff2d55" />, color: '#ff2d55', bg: 'rgba(255,45,85,0.10)', border: 'rgba(255,45,85,0.35)' },
  }[toast.type] || {};

  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-xl animate-fadeInUp"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <div className="shrink-0 mt-0.5">{cfg.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] font-black uppercase tracking-wide" style={{ color: cfg.color }}>
          {toast.title}
        </p>
        {toast.msg && (
          <p className="text-[8px] mt-0.5 leading-relaxed font-mono whitespace-pre-line" style={{ color: 'var(--text-dim)' }}>
            {toast.msg}
          </p>
        )}
      </div>
    </div>
  );
};

const PhotoGallery = ({ nik, profileUrl, onProfileChange }) => {
  const { t } = useApp();
  const [photos, setPhotos]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState(false);
  const [slotState, setSlotState] = useState({});   // { [slot]: 'uploading'|'scanning'|'ok'|'no_face'|'fail' }
  const [toast, setToast]       = useState(null);
  const toastTimer              = useRef(null);
  const inputRef                = useRef();

  useEffect(() => {
    if (!nik) { setLoading(false); return; }
    listPhotos(nik).then(p => { setPhotos(p); setLoading(false); });
  }, [nik]);

  const showToast = useCallback((type, title, msg = '') => {
    clearTimeout(toastTimer.current);
    setToast({ type, title, msg });
    if (type !== 'scanning') {
      // 'ok' & 'no_face' multi-line panjang — kasih 7 detik supaya bisa dibaca lengkap.
      const ttl = type === 'fail' ? 6000 : 7000;
      toastTimer.current = setTimeout(() => setToast(null), ttl);
    }
  }, []);

  const setSlot = (slot, state) => {
    setSlotState(prev => ({ ...prev, [slot]: state }));
    if (state === 'ok' || state === 'no_face') {
      setTimeout(() => setSlotState(prev => { const n = { ...prev }; delete n[slot]; return n; }), 5000);
    }
  };

  const nextSlot = (current) => {
    const used = new Set(current.map(p => p.slot));
    for (let i = 0; i < MAX_PHOTO_SLOTS; i++) if (!used.has(i)) return i;
    return -1;
  };

  const handleAdd = async (files) => {
    if (!nik || busy) return;
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!arr.length) return;
    setBusy(true);

    // GUARD: row penduduk WAJIB ada sebelum upload foto. Tanpa ini, foto baru
    // di penduduk yang belum disimpan akan masuk Storage tapi embedding-nya
    // gagal tersimpan diam-diam (update 0 rows). Cegah di sini untuk semua caller.
    const { data: exists } = await supabase.from('penduduk').select('nik').eq('nik', nik).maybeSingle();
    if (!exists) {
      showToast('fail', 'Simpan data penduduk dulu',
        'Foto wajah bisa diunggah setelah data penduduk tersimpan.');
      setBusy(false);
      return;
    }

    let current = [...photos];

    for (const file of arr) {
      const slot = nextSlot(current);
      if (slot < 0) break;

      // Progress tracker — semua step sync ke setiap sistem, supaya operator
      // bisa lihat persis komponen mana yang sudah sync dan mana yang belum.
      const sync = {
        storage:   false, // 1. Foto tersimpan
        ai_scan:   false, // 2. AI Engine deteksi wajah
        data_sys:  false, // 3. Embedding tersimpan di Data Sistem
        ai_memory: false, // 4. Sync ke AI Engine memory (recognition cache)
      };
      const fmtSyncMsg = () => {
        const mark = (done) => (done ? 'selesai' : 'proses');
        const lines = [
          `Foto tersimpan: ${mark(sync.storage)}`,
          `Wajah dipindai: ${mark(sync.ai_scan)}`,
          `Embedding disimpan: ${mark(sync.data_sys)}`,
          `Sinkron ke deteksi: ${mark(sync.ai_memory)}`,
        ];
        return lines.join('\n');
      };

      try {
        // ── 1. Upload ke Storage ──────────────────────────────────────────
        setSlot(slot, 'uploading');
        showToast('scanning', `Mengunggah foto · Slot ${slot + 1}`, fmtSyncMsg());
        const { path, url } = await uploadPhotoMulti(file, nik, slot);
        const entry = { path, url, slot };
        current = [...current, entry].sort((a, b) => a.slot - b.slot);
        setPhotos([...current]);
        if (current.length === 1) {
          onProfileChange(url);
          await supabase.from('penduduk').update({ foto_url: url }).eq('nik', nik);
        }
        sync.storage = true;
        showToast('scanning', 'Foto tersimpan, mulai sinkronisasi', fmtSyncMsg());

        // ── 2. AI Engine scan wajah ───────────────────────────────────────
        setSlot(slot, 'scanning');
        showToast('scanning', 'AI Engine memindai wajah...', fmtSyncMsg());

        let faceCount = 0;
        let scanResult = null;
        let usedFallback = false;
        try {
          scanResult = await analyzePhoto(file);
          faceCount = scanResult?.faces?.length ?? 0;
        } catch (e) {
          // GUARDRAIL: jalur utama gagal → fallback langsung ke Data Sistem
          console.warn('[PhotoGallery] AI scan fail:', e?.message, '— fallback ke jalur sekunder');
          showToast('scanning', 'Jalur utama sibuk, mencoba jalur cadangan', fmtSyncMsg());
          const fb = await triggerEdgeFunctionFallback(nik, path);
          if (fb?.ok) {
            faceCount   = 1;
            usedFallback = true;
            sync.ai_scan  = true;
            sync.data_sys = true; // fallback langsung simpan embedding
          }
        }

        if (faceCount === 0 && !usedFallback) {
          // /analyze bilang no face — coba sekali lagi via jalur sekunder
          console.warn('[PhotoGallery] no face dari jalur utama — coba jalur sekunder');
          showToast('scanning', 'Mencoba lagi lewat jalur cadangan', fmtSyncMsg());
          const fb = await triggerEdgeFunctionFallback(nik, path);
          if (fb?.ok) {
            faceCount   = 1;
            usedFallback = true;
            sync.ai_scan  = true;
            sync.data_sys = true;
          }
        } else if (faceCount > 0) {
          sync.ai_scan = true;
        }
        showToast('scanning', `AI Engine: ${faceCount > 0 ? `${faceCount} wajah terdeteksi` : 'tidak ada wajah'}`, fmtSyncMsg());

        // ── 3. Simpan embedding ke Data Sistem ────────────────────────────
        if (faceCount > 0 && !usedFallback) {
          setSlot(slot, 'syncing');
          showToast('scanning', 'Menyimpan ke Data Sistem...', fmtSyncMsg());
          const faceEmb = scanResult?.faces?.find(f => f.embedding)?.embedding;
          if (faceEmb) {
            await storeEmbeddingInSupabase(nik, faceEmb);
            sync.data_sys = true;
          } else {
            // /analyze return face tapi tanpa embedding — fallback
            const fb = await triggerEdgeFunctionFallback(nik, path);
            if (fb?.ok) sync.data_sys = true;
          }
          showToast('scanning', 'Data Sistem tersinkron', fmtSyncMsg());
        }

        // ── 4. VERIFIKASI READ-BACK ───────────────────────────────────────
        // Jangan percaya flag lokal — baca ulang dari DB untuk MEMASTIKAN
        // embedding benar-benar tersimpan. Inilah yang membuat notifikasi
        // "tersimpan" jujur end-to-end: badge hijau = benar2 siap dites kamera.
        let embStored = false;
        if (sync.data_sys) {
          showToast('scanning', 'Memverifikasi penyimpanan...', fmtSyncMsg());
          try {
            const { data: chk } = await supabase
              .from('penduduk').select('face_embedding').eq('nik', nik).single();
            embStored = Array.isArray(chk?.face_embedding) && chk.face_embedding.length > 0;
          } catch { embStored = false; }
        }

        // ── 5. Hasil akhir berdasarkan kondisi DB yang TERVERIFIKASI ───────
        if (embStored) {
          // AI memory = cache opsional, non-blocking. Yang menentukan: DB.
          syncFaceToHF(nik, url, slot).catch(() => {});
          sync.ai_memory = true;
          setSlot(slot, 'ok');
          showToast('ok', 'Foto tersimpan',
            'Wajah siap dipakai untuk semua fitur deteksi.');
        } else if (faceCount > 0 || sync.data_sys) {
          // Wajah terdeteksi/tersimpan tapi embedding belum terbaca di DB
          // (mis. AI engine cold-start). Foto sudah aman; auto-heal server-side
          // akan menuntaskan embedding otomatis. JUJUR: jangan klaim siap.
          setSlot(slot, 'no_face');
          showToast('no_face', 'Foto tersimpan, sedang diproses',
            'Sistem sedang menyiapkan wajah ini. Tunggu sebentar lalu muat ulang sebelum mencoba deteksi.');
        } else {
          // Benar-benar tidak ada wajah terdeteksi di foto.
          setSlot(slot, 'no_face');
          showToast('no_face', 'Foto tersimpan, wajah belum terbaca',
            'Pakai foto yang jelas dan menghadap depan. Sistem akan mencoba lagi otomatis.');
        }
      } catch (e) {
        setSlot(slot, 'fail');
        showToast('fail', 'Gagal menyimpan foto',
          (e?.message || 'Periksa koneksi') + '\n\n' + fmtSyncMsg());
      }
    }

    setBusy(false);
  };

  const handleDelete = async (photo) => {
    if (busy) return;
    setBusy(true);
    try {
      await deletePhotoSlot(photo.path);
      await deleteFaceFromHF(nik, photo.slot);
      const remaining = photos.filter(p => p.path !== photo.path);
      setPhotos(remaining);
      if (profileUrl === photo.url) onProfileChange(remaining[0]?.url ?? null);
      // Re-average remaining slots → write fresh face_embedding to Supabase.
      // If 0 slots left, write null so the panel shows "tidak ada foto" properly.
      if (remaining.length === 0) {
        await supabase.from('penduduk').update({ face_embedding: null }).eq('nik', nik);
      } else {
        syncSupabaseEmbedding(nik).catch(e => console.warn('[handleDelete] re-avg fail:', e?.message));
      }
    } catch { /* silent */ }
    setBusy(false);
  };

  const handleSetProfile = async (photo) => {
    onProfileChange(photo.url);
    await supabase.from('penduduk').update({ foto_url: photo.url }).eq('nik', nik);
  };

  if (loading) return (
    <div className="h-20 flex items-center justify-center rounded-xl"
      style={{ background: 'var(--color-surface)', border: '1px solid var(--border-dim)' }}>
      <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: '#00d4ff', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="flex flex-col gap-2">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Scan size={9} color="var(--text-mute)" />
          <p className="text-[8px] uppercase tracking-widest" style={{ color: 'var(--text-mute)' }}>
            FOTO ({photos.length}/{MAX_PHOTO_SLOTS}) · 5 angle · AI scan otomatis
          </p>
        </div>
        {photos.length < MAX_PHOTO_SLOTS && (
          <button onClick={() => inputRef.current?.click()} disabled={busy}
            className="text-[8px] font-black flex items-center gap-1" style={{ color: '#00d4ff' }}>
            <Plus size={9} /> Tambah ({POSE_LABELS[nextSlot(photos)] || ''})
          </button>
        )}
      </div>

      {/* Scan toast notification */}
      <ScanToast toast={toast} onDismiss={() => setToast(null)} />

      {/* Photo grid — sorted by slot index supaya angle order konsisten */}
      <div className="grid grid-cols-3 gap-2">
        {photos.map(photo => {
          const isProfile = profileUrl === photo.url;
          const ss = slotState[photo.slot];
          const ssCfg = ss ? SLOT_STATE[ss] : null;
          const poseLabel = POSE_LABELS[photo.slot] || `FOTO ${photo.slot + 1}`;
          return (
            <div key={photo.path} className="relative group rounded-xl overflow-hidden cursor-pointer"
              style={{ height: 88, border: `2px solid ${isProfile ? '#00d4ff' : ss ? ssCfg.color + '60' : 'var(--border-dim)'}`, background: 'var(--color-surface)' }}>
              <img src={photo.url} alt="" className="w-full h-full object-cover" />

              {/* Pose label di sudut bawah */}
              <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 text-[7px] font-black tracking-widest text-center"
                style={{ background: 'rgba(2,6,14,0.85)', color: '#00d4ff' }}>
                {poseLabel}
              </div>

              {isProfile && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[6px] font-black"
                  style={{ background: '#00d4ff', color: '#02060e' }}>PROFIL</div>
              )}

              {/* Slot state indicator */}
              {ssCfg && (
                <div className="absolute top-1 right-1 flex items-center gap-1 px-1.5 py-0.5 rounded"
                  style={{ background: 'rgba(2,6,14,0.85)' }}>
                  {ss === 'scanning' || ss === 'uploading'
                    ? <div className="w-2 h-2 border border-t-transparent rounded-full animate-spin" style={{ borderColor: ssCfg.color, borderTopColor: 'transparent' }} />
                    : <div className="w-2 h-2 rounded-full" style={{ background: ssCfg.color }} />}
                  <span className="text-[6px] font-black" style={{ color: ssCfg.color }}>{ssCfg.label}</span>
                </div>
              )}

              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: 'rgba(2,6,14,0.78)' }}>
                {!isProfile && (
                  <button onClick={() => handleSetProfile(photo)}
                    className="text-[7px] font-black px-2 py-0.5 rounded-lg w-16 transition-all hover:opacity-80"
                    style={{ background: 'rgba(0,212,255,0.2)', border: '1px solid rgba(0,212,255,0.5)', color: '#00d4ff' }}>
                    Set Profil
                  </button>
                )}
                <button onClick={() => handleDelete(photo)}
                  className="text-[7px] font-black px-2 py-0.5 rounded-lg w-16 transition-all hover:opacity-80"
                  style={{ background: 'rgba(255,45,85,0.2)', border: '1px solid rgba(255,45,85,0.5)', color: '#ff2d55' }}>
                  Hapus
                </button>
              </div>
            </div>
          );
        })}

        {photos.length < MAX_PHOTO_SLOTS && (() => {
          const nxt = nextSlot(photos);
          const nxtLabel = POSE_LABELS[nxt] || '';
          const nxtHint  = POSE_HINTS[nxt] || '';
          return (
            <button onClick={() => inputRef.current?.click()} disabled={busy}
              className="rounded-xl flex flex-col items-center justify-center gap-1 transition-all hover:opacity-80"
              style={{ height: 88, border: '2px dashed var(--border-mid)', background: 'var(--color-surface)', opacity: busy ? 0.5 : 1 }}>
              {busy
                ? <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: '#00d4ff', borderTopColor: 'transparent' }} />
                : <Upload size={14} color="var(--text-dim)" />}
              <span className="text-[8px] font-black tracking-widest" style={{ color: '#00d4ff' }}>
                {busy ? t('processing_dots') : nxtLabel}
              </span>
              {!busy && nxtHint && (
                <span className="text-[6px] text-center px-1" style={{ color: 'var(--text-mute)' }}>
                  {nxtHint}
                </span>
              )}
            </button>
          );
        })()}
      </div>

      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={e => { handleAdd(e.target.files); e.target.value = ''; }} />
    </div>
  );
};

export default PhotoGallery;
